import { useState, useEffect, useCallback } from "react";
import { Users, UserPlus, TrendingUp, DollarSign, Link2, Loader2, Plus, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4" data-testid={testid}>
      <div className="text-2xl font-heading text-white">{value}</div>
      <div className="text-xs text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

function Bars({ title, rows, valueKey = "count", testid }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-5" data-testid={testid}>
      <h3 className="font-heading text-lg text-white mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#52525B]">No data yet.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.key}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[#D4D4D8] truncate max-w-[70%]">{r.key || "direct"}</span>
                <span className="text-[#A1A1AA]">{valueKey === "mrr" ? `$${(r[valueKey] || 0).toFixed(2)}` : r[valueKey]}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full fv-gold-gradient" style={{ width: `${((r[valueKey] || 0) / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAcquisition() {
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pc, setPc] = useState(null);
  const [rt, setRt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pType, setPType] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, l, p, c, r] = await Promise.all([
        api.get("/admin/acquisition/funnel"),
        api.get("/admin/acquisition/leads"),
        api.get("/admin/partners"),
        api.get("/admin/acquisition/paid-conversion"),
        api.get("/admin/retention/overview"),
      ]);
      setData(f.data); setLeads(l.data.leads || []); setPartners(p.data.partners || []); setPc(c.data); setRt(r.data);
    } catch (e) {
      toast.error("Failed to load acquisition data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPartner = async () => {
    if (!pName.trim()) { toast.error("Partner name required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/partners", { name: pName, email: pEmail, type: pType });
      setPName(""); setPEmail(""); setPType("");
      toast.success("Partner created (commissions disabled until approved)");
      load();
    } catch (e) { toast.error("Failed to create partner"); }
    finally { setCreating(false); }
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  }

  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="admin-acquisition">
      <div>
        <h2 className="font-heading text-2xl text-white">Customer Acquisition</h2>
        <p className="text-sm text-[#71717A]">Visitors → Leads → Accounts → Checkout → Paid. Attribution is first/last-touch. Empty until real data accrues.</p>
      </div>

      {/* Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="acq-funnel">
        {data.funnel.map((s) => (
          <div key={s.step} className="rounded-xl border border-white/10 bg-[#121214] p-4">
            <div className="text-2xl font-heading text-white">{s.count}</div>
            <div className="text-xs text-[#71717A] mt-1">{s.step}</div>
            <div className="text-[10px] text-[#D4AF37] mt-1">{s.conversion}% from prev</div>
          </div>
        ))}
      </div>

      {/* Key rates */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Visitor → Lead" value={`${data.rates.visitor_to_lead}%`} testid="rate-v2l" />
        <Stat label="Lead → Account" value={`${data.rates.lead_to_account}%`} testid="rate-l2a" />
        <Stat label="Account → Paid" value={`${data.rates.account_to_paid}%`} testid="rate-a2p" />
        <Stat label="Visitor → Paid" value={`${data.rates.visitor_to_paid}%`} testid="rate-v2p" />
      </div>

      {/* Paid-conversion (Phase 6) */}
      {pc && (
        <div className="rounded-xl border border-[#D4AF37]/20 bg-[#121214] p-5 space-y-4" data-testid="paid-conversion">
          <div>
            <h3 className="font-heading text-lg text-white">Paid conversion · Pricing → Plan → Checkout → Paid</h3>
            <p className="text-xs text-[#71717A]">{pc.basis}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Pricing views" value={pc.totals.pricing_views} testid="pc-pricing-views" />
            <Stat label="Plan selected" value={pc.totals.plan_selected} testid="pc-plan-selected" />
            <Stat label="Checkout started" value={pc.totals.checkout_starts} testid="pc-checkout-starts" />
            <Stat label="Checkout completed" value={pc.totals.checkout_completed} testid="pc-checkout-completed" />
            <Stat label="Verified paid" value={pc.totals.verified_paid} testid="pc-verified-paid" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Pricing → Plan" value={`${pc.rates.pricing_to_plan}%`} testid="pc-rate-pricing-plan" />
            <Stat label="Plan → Checkout" value={`${pc.rates.plan_to_checkout}%`} testid="pc-rate-plan-checkout" />
            <Stat label="Checkout → Paid" value={`${pc.rates.checkout_to_paid}%`} testid="pc-rate-checkout-paid" />
            <Stat label="Pricing → Paid" value={`${pc.rates.pricing_to_paid}%`} testid="pc-rate-pricing-paid" />
          </div>
          <div className="rounded-lg border border-white/8 bg-[#0d0d0f] overflow-x-auto" data-testid="pc-by-plan">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#71717A] text-xs">
                <th className="py-2 px-3">Plan</th><th>Selected</th><th>Checkout started</th><th>Checkout completed</th><th>Verified paid</th>
              </tr></thead>
              <tbody>
                {pc.by_plan.map((r) => (
                  <tr key={r.plan} className="border-t border-white/5 text-[#D4D4D8]" data-testid={`pc-plan-${r.plan}`}>
                    <td className="py-2 px-3 capitalize">{r.plan}</td><td>{r.selected}</td>
                    <td>{r.checkout_starts}</td><td>{r.checkout_completed}</td><td>{r.verified_paid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Bars title="Verified paid by acquisition source" rows={pc.by_source} testid="pc-by-source" />
        </div>
      )}

      {/* Retention & expansion (Phase 7) */}
      {rt && (
        <div className="rounded-xl border border-emerald-500/20 bg-[#121214] p-5 space-y-4" data-testid="retention-overview">
          <div>
            <h3 className="font-heading text-lg text-white">Retention & expansion · verified paid only</h3>
            <p className="text-xs text-[#71717A]">{rt.basis}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Verified paid" value={rt.verified_paid_subscribers} testid="rt-verified-paid" />
            <Stat label="New paid today" value={rt.new_paid_today} testid="rt-new-paid-today" />
            <Stat label="First Win started" value={rt.first_win.started} testid="rt-fw-started" />
            <Stat label="First Win completed" value={rt.first_win.completed} testid="rt-fw-completed" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="First Win rate" value={rt.first_win.completion_rate == null ? "—" : `${rt.first_win.completion_rate}%`} testid="rt-fw-rate" />
            <Stat label="Median hrs to win" value={rt.first_win.median_hours_to_complete == null ? "—" : rt.first_win.median_hours_to_complete} testid="rt-fw-median" />
            <Stat label="D1 active" value={rt.retention.d1_rate == null ? "—" : `${rt.retention.d1_rate}%`} testid="rt-d1" />
            <Stat label="Inactive paid" value={rt.retention.inactive_paid_subscribers} testid="rt-inactive" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["d7", "d14", "d30"].map((k) => (
              <Stat key={k} label={`${k.toUpperCase()} retention`} testid={`rt-${k}`}
                value={rt.retention[k].rate == null ? "Not yet measurable" : `${rt.retention[k].rate}% (${rt.retention[k].retained}/${rt.retention[k].mature_cohort})`} />
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Re-engage attempts" value={rt.reengagement.attempts} testid="rt-reeng-attempts" />
            <Stat label="Re-engage recoveries" value={rt.reengagement.recoveries} testid="rt-reeng-recov" />
            <Stat label="Cancel interventions" value={rt.cancellation.intervention_shown} testid="rt-cancel-shown" />
            <Stat label="Cancel saves" value={rt.cancellation.saves} testid="rt-cancel-saves" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Downgrades" value={rt.cancellation.downgrades} testid="rt-downgrades" />
            <Stat label="Cancellations" value={rt.cancellation.cancellations} testid="rt-cancellations" />
            <Stat label="Upgrade opportunities" value={rt.expansion.opportunities} testid="rt-upgrade-opp" />
            <Stat label="Upgrades accepted" value={rt.expansion.accepted} testid="rt-upgrade-acc" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Expansion MRR" value={`$${rt.expansion.expansion_mrr}`} testid="rt-expansion-mrr" />
            <Stat label="Contraction MRR" value={`$${rt.expansion.contraction_mrr}`} testid="rt-contraction-mrr" />
            <Stat label="Churned MRR" value={`$${rt.expansion.churned_mrr}`} testid="rt-churned-mrr" />
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Bars title="Traffic by first-touch source" rows={data.by_source} testid="acq-by-source" />
        <Bars title="MRR by acquisition source" rows={data.mrr_by_source} valueKey="mrr" testid="acq-mrr" />
        <Bars title="Real MRR by free tool" rows={data.mrr_by_tool || []} valueKey="mrr" testid="acq-mrr-tool" />
        <Bars title="Leads by free tool" rows={data.by_tool} testid="acq-by-tool" />
        <Bars title="Leads by segment" rows={data.by_segment} testid="acq-by-segment" />
      </div>

      {/* Referral + partner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Referral signups" value={data.referral.signups} testid="acq-ref-signups" />
        <Stat label="Referral qualified" value={data.referral.qualified} testid="acq-ref-qualified" />
        <Stat label="Partner signups" value={data.partner.signups} testid="acq-partner-signups" />
        <Stat label="Partner paid" value={data.partner.paid} testid="acq-partner-paid" />
      </div>

      {/* Partners */}
      <div className="rounded-xl border border-white/10 bg-[#121214] p-5" data-testid="acq-partners">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="font-heading text-lg text-white">Affiliate / Partner tracking</h3>
        </div>
        <p className="text-xs text-[#F59E0B] mb-4">Cash commissions are DISABLED — tracking foundation only. Amounts/payout rules require owner approval before activation.</p>
        <div className="grid sm:grid-cols-4 gap-2 mb-4">
          <Input data-testid="partner-name" placeholder="Partner name" value={pName} onChange={(e) => setPName(e.target.value)}
            className="bg-[#0A0A0B] border-white/10 text-white" />
          <Input data-testid="partner-email" placeholder="Email (optional)" value={pEmail} onChange={(e) => setPEmail(e.target.value)}
            className="bg-[#0A0A0B] border-white/10 text-white" />
          <Input data-testid="partner-type" placeholder="Type (e.g. Coach)" value={pType} onChange={(e) => setPType(e.target.value)}
            className="bg-[#0A0A0B] border-white/10 text-white" />
          <Button onClick={createPartner} disabled={creating} data-testid="partner-create"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
        {partners.length === 0 ? (
          <p className="text-sm text-[#52525B]">No partners yet.</p>
        ) : (
          <div className="space-y-2">
            {partners.map((p) => (
              <div key={p.partner_id} className="flex items-center justify-between rounded-lg bg-[#0A0A0B] border border-white/5 p-3" data-testid={`partner-row-${p.code}`}>
                <div>
                  <div className="text-white text-sm">{p.name} <span className="text-[#71717A]">· {p.code}</span></div>
                  <div className="text-xs text-[#71717A]">{p.stats.clicks} clicks · {p.stats.signups} signups · {p.stats.paid} paid</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(p.share_url); toast.success("Link copied"); }}
                  className="text-[#D4AF37]"><Copy className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent leads */}
      <div className="rounded-xl border border-white/10 bg-[#121214] p-5" data-testid="acq-leads">
        <h3 className="font-heading text-lg text-white mb-4">Recent leads ({leads.length})</h3>
        {leads.length === 0 ? (
          <p className="text-sm text-[#52525B]">No leads captured yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#71717A] text-xs">
                <th className="py-2">Email</th><th>Name</th><th>Source</th><th>Tool</th><th>Segment</th><th>Status</th><th>Nurture</th>
              </tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.lead_id} className="border-t border-white/5 text-[#D4D4D8]">
                    <td className="py-2">{l.email}</td><td>{l.first_name || "—"}</td><td>{l.source}</td>
                    <td>{l.tool_used || "—"}</td><td>{l.segment}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded-full bg-white/5">{l.status}</span></td>
                    <td>{l.nurture_step}/6</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
